// Starts each product card's main photo loading before it is near the screen.
//
// Cards ship loading="lazy" so a long grid does not fetch every photo up front. Chrome starts a lazy
// image roughly 1250 to 2500 px before it is visible, so a scroll never catches it. In the iPhone
// simulator, on a first visit with nothing cached, cards reached by a fast flick showed empty cream
// wells until their lazy images arrived. This watches every lazy main image and switches it to eager
// once it is close, which makes the browser fetch it at once. Cards added later (filters, pagination,
// search, related rows) are picked up by a MutationObserver. The hover image (.pm-card__img--alt) and
// every other image are left alone.
//
// What "close" means:
//   - Cards in the normal flow: two screens ahead or behind vertically (rootMargin 200% 100%, the
//     percentages are of the viewport's own height and width), one screen sideways.
//   - Cards inside a horizontal scroller (overflow-x auto or scroll, such as the related row on a
//     product page): an IntersectionObserver clips an image by its scroller, so a card scrolled out of
//     the row never counts as near. The row itself is watched instead, with the same margin, and when
//     it nears the viewport every lazy card photo in it is switched at once. Only the closest
//     scroller is used, and only one that is wider than its box (a vertical scroller computes
//     overflow-x auto too, and its cards keep their own observation).
//   - The related row scrolls only at phone width, so crossing 749px (a rotation) re-reads the layout.
(function () {
  if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;

  var SELECTOR = 'img.pm-card__img--main[loading="lazy"]';

  // Closest horizontal scroller at or above each element looked at, or null for none, so
  // getComputedStyle runs once per element. rows holds every scroller being handled: false while it is
  // watched, true once its cards were switched.
  var scrollerOf = new WeakMap();
  var rows = new WeakMap();

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var target = entry.target;
        observer.unobserve(target);
        if (!rows.has(target)) {
          target.loading = 'eager';
          return;
        }
        rows.set(target, true);
        target.querySelectorAll(SELECTOR).forEach(function (img) {
          if (closestScroller(img.parentElement) === target) img.loading = 'eager';
        });
      });
    },
    { rootMargin: '200% 100%' }
  );

  // The closest ancestor-or-self of node with overflow-x auto or scroll, stopping short of <body>.
  function closestScroller(node) {
    var path = [];
    var found = null;
    for (; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
      if (scrollerOf.has(node)) {
        found = scrollerOf.get(node);
        break;
      }
      path.push(node);
      var overflowX = getComputedStyle(node).overflowX;
      if ((overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth) {
        found = node;
        break;
      }
    }
    path.forEach(function (visited) {
      scrollerOf.set(visited, found);
    });
    return found;
  }

  // A lazy main image either joins its row's single observation or, in the normal flow, is watched
  // itself. Watching the same node twice does nothing.
  function watchImage(img) {
    var row = closestScroller(img.parentElement);
    if (!row) {
      observer.observe(img);
    } else if (!rows.has(row)) {
      rows.set(row, false);
      observer.observe(row);
    } else if (rows.get(row)) {
      img.loading = 'eager';
    }
  }

  // Watch the node itself when it is a lazy main image, and every one inside it.
  function watch(node) {
    if (node.matches(SELECTOR)) watchImage(node);
    node.querySelectorAll(SELECTOR).forEach(watchImage);
  }

  watch(document.documentElement);

  var phone = window.matchMedia('(max-width: 749px)');
  if (phone.addEventListener) {
    phone.addEventListener('change', function () {
      scrollerOf = new WeakMap();
      rows = new WeakMap();
      watch(document.documentElement);
    });
  }

  // Only added elements are looked at, so a change that adds no card costs one selector scan per added
  // subtree.
  new MutationObserver(function (records) {
    records.forEach(function (record) {
      record.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) watch(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
